import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Modal,
  Alert,
  Badge,
  ProgressBar,
  Tabs,
  Tab,
} from "react-bootstrap";
import api from "../services/api";
import { exportPollToExcel, PollVoterData } from "../utils/exportPollToExcel";
import { useLanguage } from "../i18n/LanguageContext";
import { type TranslationKey } from "../i18n/translations";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PollOptionForm {
  option_text: string;
  image_url?: string;
  image_file?: File;
}

interface Poll {
  id: number;
  title: string;
  description?: string;
  status: string;
  allow_multiple: boolean;
  show_results_before_vote: boolean;
  closes_at?: string;
  created_at: string;
  parent_poll_id?: number;
  trigger_option_ids?: string;
}

interface PollWithResults extends Poll {
  total_votes: number;
  options: Array<{ id: string; option_text: string; image_url?: string }>;
  results: Array<{
    option_id: string;
    option_text: string;
    image_url?: string;
    vote_count: number;
    percentage: number;
  }>;
}

interface MetaValues {
  title: string;
  description: string;
  allow_multiple: boolean;
  closes_at: string;
}

// ─── Sub-components (defined OUTSIDE AdminPolls to prevent remount on render) ─

interface PollMetaFieldsProps {
  values: MetaValues;
  onChange: (k: string, v: any) => void;
  t: (key: TranslationKey) => string;
}

const PollMetaFields: React.FC<PollMetaFieldsProps> = ({
  values,
  onChange,
  t,
}) => (
  <>
    <Form.Group className="mb-3">
      <Form.Label>{t("pollTitle")}</Form.Label>
      <Form.Control
        type="text"
        value={values.title}
        onChange={(e) => onChange("title", e.target.value)}
      />
    </Form.Group>
    <Form.Group className="mb-3">
      <Form.Label>{t("pollDescription")}</Form.Label>
      <Form.Control
        as="textarea"
        rows={2}
        value={values.description}
        onChange={(e) => onChange("description", e.target.value)}
      />
    </Form.Group>
    <Form.Group className="mb-3">
      <Form.Check
        type="checkbox"
        label={t("allowMultipleChoice")}
        checked={values.allow_multiple}
        onChange={(e) => onChange("allow_multiple", e.target.checked)}
      />
    </Form.Group>
    <Form.Group className="mb-3">
      <Form.Label>{t("pollClosesAt")}</Form.Label>
      <Form.Control
        type="datetime-local"
        value={values.closes_at}
        onChange={(e) => onChange("closes_at", e.target.value)}
      />
    </Form.Group>
  </>
);

interface OptionGridProps {
  options: PollOptionForm[];
  setOptions: React.Dispatch<React.SetStateAction<PollOptionForm[]>>;
  fileRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  idPrefix: string;
  t: (key: TranslationKey) => string;
  onImageSelect: (
    index: number,
    file: File,
    setOptions: React.Dispatch<React.SetStateAction<PollOptionForm[]>>,
  ) => void;
  onRemoveImage: (
    index: number,
    setOptions: React.Dispatch<React.SetStateAction<PollOptionForm[]>>,
  ) => void;
}

const OptionGrid: React.FC<OptionGridProps> = ({
  options,
  setOptions,
  fileRefs,
  idPrefix,
  t,
  onImageSelect,
  onRemoveImage,
}) => {
  const isValid = (o: PollOptionForm) =>
    !!(o.option_text.trim() || o.image_file || o.image_url);

  const addOpt = () => {
    if (options.length < 10)
      setOptions((prev) => [...prev, { option_text: "" }]);
  };
  const removeOpt = (i: number) => {
    if (options.length > 2)
      setOptions((prev) => prev.filter((_, idx) => idx !== i));
  };
  const updateOpt = (i: number, val: string) => {
    setOptions((prev) => {
      const u = [...prev];
      u[i] = { ...u[i], option_text: val };
      return u;
    });
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="mb-0">
          {t("pollOptions")}{" "}
          <span className="text-muted small">({t("optionsHint")})</span>
        </Form.Label>
        {options.length < 10 && (
          <Button variant="outline-primary" size="sm" onClick={addOpt}>
            + {t("addOption")}
          </Button>
        )}
      </div>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}
      >
        {options.map((option, index) => (
          <div
            key={`${idPrefix}-${index}`}
            className="border rounded"
            style={{
              background: "#fff",
              overflow: "hidden",
              position: "relative",
              boxShadow: isValid(option) ? "0 0 0 2px #0d6efd33" : "none",
            }}
          >
            {options.length > 2 && (
              <button
                type="button"
                onClick={() => removeOpt(index)}
                style={{
                  position: "absolute",
                  top: "6px",
                  right: "6px",
                  zIndex: 10,
                  background: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: "22px",
                  height: "22px",
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            )}
            <div
              style={{
                position: "relative",
                paddingTop: "100%",
                background: "#f0f2f5",
                cursor: "pointer",
              }}
              onClick={() => fileRefs.current[index]?.click()}
            >
              {option.image_url ? (
                <>
                  <img
                    src={option.image_url}
                    alt={`Option ${index + 1}`}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(0,0,0,0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
                  >
                    <span
                      style={{
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {t("changePhoto")}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveImage(index, setOptions);
                    }}
                    style={{
                      position: "absolute",
                      bottom: "6px",
                      right: "6px",
                      background: "rgba(220,53,69,0.85)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "2px 7px",
                      fontSize: "11px",
                      cursor: "pointer",
                    }}
                  >
                    {t("removePhoto")}
                  </button>
                </>
              ) : (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#adb5bd",
                  }}
                >
                  <span style={{ fontSize: "28px" }}>📷</span>
                  <span style={{ fontSize: "11px", marginTop: "4px" }}>
                    {t("clickToAddPhoto")}
                  </span>
                </div>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={(el) => (fileRefs.current[index] = el)}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onImageSelect(index, f, setOptions);
              }}
            />
            <div style={{ padding: "8px" }}>
              <Form.Control
                type="text"
                value={option.option_text}
                onChange={(e) => updateOpt(index, e.target.value)}
                placeholder={t("captionOptional")}
                size="sm"
                style={{ fontSize: "0.8rem", border: "1px solid #dee2e6" }}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const AdminPolls: React.FC = () => {
  const { t, dir } = useLanguage();

  const createFileRefs = useRef<(HTMLInputElement | null)[]>([]);
  const editFileRefs = useRef<(HTMLInputElement | null)[]>([]);
  const phase2FileRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [polls, setPolls] = useState<Poll[]>([]);
  const [selectedPoll, setSelectedPoll] = useState<PollWithResults | null>(
    null,
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showPhase2Modal, setShowPhase2Modal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Create form
  const [newPoll, setNewPoll] = useState<MetaValues>({
    title: "",
    description: "",
    allow_multiple: false,
    closes_at: "",
  });
  const [createOptions, setCreateOptions] = useState<PollOptionForm[]>([
    { option_text: "" },
    { option_text: "" },
  ]);

  // Edit form
  const [editPoll, setEditPoll] = useState<Poll | null>(null);
  const [editMeta, setEditMeta] = useState<MetaValues>({
    title: "",
    description: "",
    closes_at: "",
    allow_multiple: false,
  });
  const [editOptions, setEditOptions] = useState<PollOptionForm[]>([]);

  // Phase 2 form
  const [phase2Parent, setPhase2Parent] = useState<Poll | null>(null);
  const [phase2ParentResults, setPhase2ParentResults] =
    useState<PollWithResults | null>(null);
  const [phase2TriggerIds, setPhase2TriggerIds] = useState<string[]>([]);
  const [phase2Poll, setPhase2Poll] = useState<MetaValues>({
    title: "",
    description: "",
    allow_multiple: false,
    closes_at: "",
  });
  const [phase2Options, setPhase2Options] = useState<PollOptionForm[]>([
    { option_text: "" },
    { option_text: "" },
  ]);

  useEffect(() => {
    loadPolls();
  }, []);

  const loadPolls = async () => {
    try {
      const response = await api.get("/polls");
      setPolls(response.data.polls);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load polls");
    }
  };

  // ─── Image helpers (stable references — defined once at component level) ────

  const handleImageSelect = (
    index: number,
    file: File,
    setOptions: React.Dispatch<React.SetStateAction<PollOptionForm[]>>,
  ) => {
    setOptions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        image_file: file,
        image_url: URL.createObjectURL(file),
      };
      return updated;
    });
  };

  const removeImage = (
    index: number,
    setOptions: React.Dispatch<React.SetStateAction<PollOptionForm[]>>,
  ) => {
    setOptions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        image_file: undefined,
        image_url: undefined,
      };
      return updated;
    });
  };

  const processOptions = async (options: PollOptionForm[]) => {
    const valid = options.filter(
      (o) => o.option_text.trim() || o.image_file || o.image_url,
    );
    return Promise.all(
      valid.map(async (opt) => {
        let image_url = opt.image_url;
        if (opt.image_file) {
          const fd = new FormData();
          fd.append("image", opt.image_file);
          const res = await api.post("/uploads/image", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          image_url = res.data.url;
        }
        return { option_text: opt.option_text || "", image_url };
      }),
    );
  };

  // ─── Create ────────────────────────────────────────────────────────────────

  const handleCreatePoll = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    const validOptions = createOptions.filter(
      (o) => o.option_text.trim() || o.image_file || o.image_url,
    );
    if (validOptions.length < 2) {
      setError("Please provide at least 2 options");
      setLoading(false);
      return;
    }
    try {
      const options = await processOptions(createOptions);
      await api.post("/polls", {
        ...newPoll,
        closes_at: newPoll.closes_at || undefined,
        options,
      });
      setSuccess(t("pollCreated"));
      setShowCreateModal(false);
      setNewPoll({
        title: "",
        description: "",
        allow_multiple: false,
        closes_at: "",
      });
      setCreateOptions([{ option_text: "" }, { option_text: "" }]);
      loadPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create poll");
    } finally {
      setLoading(false);
    }
  };

  // ─── Edit ──────────────────────────────────────────────────────────────────

  const openEditModal = async (poll: Poll) => {
    try {
      const res = await api.get(`/polls/${poll.id}`);
      const data: PollWithResults = res.data.poll ?? res.data;
      setEditPoll(data);
      setEditMeta({
        title: data.title || "",
        description: data.description || "",
        closes_at: data.closes_at ? data.closes_at.slice(0, 16) : "",
        allow_multiple: data.allow_multiple,
      });
      setEditOptions(
        data.options.map((o) => ({
          option_text: o.option_text,
          image_url: o.image_url,
        })),
      );
      setShowEditModal(true);
    } catch {
      setError("Failed to load poll for editing");
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editPoll) return;
    setError("");
    setLoading(true);
    try {
      await api.put(`/polls/${editPoll.id}`, {
        title: editMeta.title,
        description: editMeta.description,
        closes_at: editMeta.closes_at || undefined,
        allow_multiple: editMeta.allow_multiple,
      });
      const processedOptions = await processOptions(editOptions);
      if (processedOptions.length >= 2) {
        await api.put(`/polls/${editPoll.id}/options`, {
          options: processedOptions,
        });
      }
      setSuccess(t("pollUpdated"));
      setShowEditModal(false);
      loadPolls();
    } catch (err: any) {
      if (err.response?.status === 404) {
        setSuccess(t("pollUpdated"));
        setShowEditModal(false);
        loadPolls();
      } else {
        setError(err.response?.data?.error || "Failed to update poll");
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Status changes ────────────────────────────────────────────────────────

  const handleStatusChange = async (pollId: number, newStatus: string) => {
    try {
      await api.put(`/polls/${pollId}`, { status: newStatus });
      setSuccess(
        newStatus === "active"
          ? t("pollActivated")
          : newStatus === "closed"
            ? t("pollClosed")
            : t("pollUpdated"),
      );
      loadPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update poll status");
    }
  };

  const handleDeletePoll = async (pollId: number) => {
    if (!window.confirm(t("confirmDeletePoll"))) return;
    try {
      await api.delete(`/polls/${pollId}`);
      setSuccess(t("pollDeleted"));
      loadPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete poll");
    }
  };

  const handleViewResults = async (pollId: number) => {
    try {
      const response = await api.get(`/polls/${pollId}`);
      setSelectedPoll(response.data.poll ?? response.data);
      setShowResultsModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load poll results");
    }
  };

  const handleExportResults = async (pollId: number) => {
    setExporting(true);
    try {
      const response = await api.get(`/polls/${pollId}/voters`);
      await exportPollToExcel(response.data as PollVoterData);
    } catch {
      setError("Failed to export poll results");
    } finally {
      setExporting(false);
    }
  };

  // ─── Phase 2 ───────────────────────────────────────────────────────────────

  const openPhase2Modal = async (poll: Poll) => {
    try {
      const res = await api.get(`/polls/${poll.id}`);
      const data: PollWithResults = res.data.poll ?? res.data;
      setPhase2Parent(poll);
      setPhase2ParentResults(data);
      setPhase2TriggerIds([]);
      setPhase2Poll({
        title: "",
        description: "",
        allow_multiple: false,
        closes_at: "",
      });
      setPhase2Options([{ option_text: "" }, { option_text: "" }]);
      setShowPhase2Modal(true);
    } catch {
      setError("Failed to load poll for phase 2");
    }
  };

  const handleCreatePhase2 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phase2Parent) return;
    if (phase2TriggerIds.length === 0) {
      setError("Select at least one trigger option for phase 2");
      return;
    }
    const validOptions = phase2Options.filter(
      (o) => o.option_text.trim() || o.image_file || o.image_url,
    );
    if (validOptions.length < 2) {
      setError("Please provide at least 2 options");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const options = await processOptions(phase2Options);
      await api.post("/polls", {
        ...phase2Poll,
        closes_at: phase2Poll.closes_at || undefined,
        options,
        parent_poll_id: phase2Parent.id,
        trigger_option_ids: JSON.stringify(phase2TriggerIds),
      });
      setSuccess(t("phase2Created"));
      setShowPhase2Modal(false);
      loadPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create phase 2");
    } finally {
      setLoading(false);
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      draft: "secondary",
      active: "success",
      closed: "danger",
    };
    return (
      <Badge bg={variants[status] || "secondary"}>{status.toUpperCase()}</Badge>
    );
  };

  const getChildren = (pollId: number) =>
    polls.filter((p) => p.parent_poll_id === pollId);

  // ─── Action buttons ────────────────────────────────────────────────────────

  const PollActionButtons = ({
    poll,
    mobile = false,
  }: {
    poll: Poll;
    mobile?: boolean;
  }) => {
    const children = getChildren(poll.id);
    const size = "sm" as const;
    return (
      <div className="d-flex gap-2 flex-wrap">
        <Button
          size={size}
          variant="outline-primary"
          onClick={() => handleViewResults(poll.id)}
        >
          {mobile ? "📊 " : ""}
          {t("results")}
        </Button>
        {(poll.status === "draft" || poll.status === "active") && (
          <Button
            size={size}
            variant="outline-secondary"
            onClick={() => openEditModal(poll)}
          >
            ✏️ {t("edit")}
          </Button>
        )}
        {poll.status === "draft" && (
          <Button
            size={size}
            variant="success"
            onClick={() => handleStatusChange(poll.id, "active")}
          >
            ✓ {t("activate")}
          </Button>
        )}
        {poll.status === "active" && (
          <Button
            size={size}
            variant="warning"
            onClick={() => handleStatusChange(poll.id, "closed")}
          >
            ⏸ {t("closePoll")}
          </Button>
        )}
        {poll.status === "closed" && (
          <Button
            size={size}
            variant="outline-success"
            onClick={() => handleStatusChange(poll.id, "active")}
          >
            🔄 {t("reactivate")}
          </Button>
        )}
        {!poll.parent_poll_id && children.length === 0 && (
          <Button
            size={size}
            variant="outline-info"
            onClick={() => openPhase2Modal(poll)}
          >
            🔗 {t("addPhase2")}
          </Button>
        )}
        {children.length > 0 && (
          <Badge bg="info" className="d-flex align-items-center px-2">
            🔗 {t("hasPhase2")}
          </Badge>
        )}
        <Button
          size={size}
          variant="outline-danger"
          onClick={() => handleDeletePoll(poll.id)}
        >
          🗑 {mobile ? "" : t("delete")}
        </Button>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Container fluid className="p-3" dir={dir}>
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h4 className="mb-1" style={{ fontWeight: "600" }}>
                {t("pollsManagement")}
              </h4>
              <p className="text-muted small mb-0">
                {t("pollsManagementSubtitle")}
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              + {t("createPoll")}
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError("")}
          className="mb-3"
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccess("")}
          className="mb-3"
        >
          {success}
        </Alert>
      )}

      {/* ── Mobile cards ── */}
      <div className="d-md-none">
        {polls.length === 0 ? (
          <Card>
            <Card.Body className="text-center py-5">
              <div style={{ fontSize: "48px" }}>📊</div>
              <h5 className="text-muted mt-3">{t("noPollsYet")}</h5>
              <p className="text-muted small">{t("noPollsYetDesc")}</p>
            </Card.Body>
          </Card>
        ) : (
          polls.map((poll) => (
            <Card key={poll.id} className="mb-3 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div style={{ flex: 1 }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      {poll.parent_poll_id && (
                        <Badge bg="info" pill style={{ fontSize: "10px" }}>
                          ↳ {t("phase2Label")}
                        </Badge>
                      )}
                      <h6 className="mb-0" style={{ fontWeight: "600" }}>
                        {poll.title || (
                          <em className="text-muted">{t("untitled")}</em>
                        )}
                      </h6>
                    </div>
                    {poll.description && (
                      <p className="text-muted small mb-2">
                        {poll.description}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(poll.status)}
                </div>
                <div className="d-flex gap-2 mb-2 flex-wrap small text-muted">
                  <span>
                    📅 {new Date(poll.created_at).toLocaleDateString()}
                  </span>
                  {poll.closes_at && (
                    <span>
                      ⏰ {t("pollCloses")}:{" "}
                      {new Date(poll.closes_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <PollActionButtons poll={poll} mobile />
              </Card.Body>
            </Card>
          ))
        )}
      </div>

      {/* ── Desktop table ── */}
      <div className="d-none d-md-block">
        <Row className="justify-content-center">
          <Col xs={12} xl={11}>
            <Card className="shadow-sm">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("pollTitle_label")}</th>
                        <th>{t("pollStatus")}</th>
                        <th>{t("createdAt")}</th>
                        <th>{t("pollCloses")}</th>
                        <th>{t("pollType")}</th>
                        <th>{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {polls.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="text-center text-muted py-4"
                          >
                            {t("noPollsYet")}. {t("noPollsYetDesc")}
                          </td>
                        </tr>
                      ) : (
                        polls.map((poll) => (
                          <tr key={poll.id}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {poll.parent_poll_id && (
                                  <Badge
                                    bg="info"
                                    pill
                                    style={{ fontSize: "10px" }}
                                  >
                                    ↳ {t("phase2Label")}
                                  </Badge>
                                )}
                                <div>
                                  <strong>
                                    {poll.title || (
                                      <em className="text-muted">
                                        {t("untitled")}
                                      </em>
                                    )}
                                  </strong>
                                  {poll.description && (
                                    <div className="small text-muted">
                                      {poll.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>{getStatusBadge(poll.status)}</td>
                            <td>
                              <small>
                                {new Date(poll.created_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <small>
                                {poll.closes_at
                                  ? new Date(
                                      poll.closes_at,
                                    ).toLocaleDateString()
                                  : "—"}
                              </small>
                            </td>
                            <td>
                              {poll.allow_multiple && (
                                <Badge bg="info" pill>
                                  {t("multi")}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <PollActionButtons poll={poll} />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* ══ CREATE MODAL ══ */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        size="lg"
        dir={dir}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("createNewPoll")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="create-poll-form" onSubmit={handleCreatePoll}>
            <PollMetaFields
              values={newPoll}
              onChange={(k, v) => setNewPoll((p) => ({ ...p, [k]: v }))}
              t={t}
            />
            <Form.Group className="mb-3">
              <OptionGrid
                options={createOptions}
                setOptions={setCreateOptions}
                fileRefs={createFileRefs}
                idPrefix="create"
                t={t}
                onImageSelect={handleImageSelect}
                onRemoveImage={removeImage}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="create-poll-form"
            disabled={loading}
          >
            {loading ? t("creating") : t("createPoll")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══ EDIT MODAL ══ */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        dir={dir}
      >
        <Modal.Header closeButton>
          <Modal.Title>✏️ {t("editPoll")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editPoll && (
            <Form id="edit-poll-form" onSubmit={handleSaveEdit}>
              <Tabs defaultActiveKey="meta" className="mb-3">
                <Tab eventKey="meta" title={t("pollDetails")}>
                  <div className="pt-2">
                    <PollMetaFields
                      values={editMeta}
                      onChange={(k, v) =>
                        setEditMeta((p) => ({ ...p, [k]: v }))
                      }
                      t={t}
                    />
                  </div>
                </Tab>
                <Tab eventKey="options" title={t("pollOptions")}>
                  <div className="pt-2">
                    <Alert variant="info" className="small py-2 mb-3">
                      ℹ️ {t("editOptionsNote")}
                    </Alert>
                    <OptionGrid
                      options={editOptions}
                      setOptions={setEditOptions}
                      fileRefs={editFileRefs}
                      idPrefix="edit"
                      t={t}
                      onImageSelect={handleImageSelect}
                      onRemoveImage={removeImage}
                    />
                  </div>
                </Tab>
              </Tabs>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-poll-form"
            disabled={loading}
          >
            {loading ? t("saving") : t("saveChanges")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══ RESULTS MODAL ══ */}
      <Modal
        show={showResultsModal}
        onHide={() => setShowResultsModal(false)}
        size="lg"
        dir={dir}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("pollResults")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPoll && (
            <>
              {selectedPoll.title && <h5>{selectedPoll.title}</h5>}
              {selectedPoll.description && (
                <p className="text-muted">{selectedPoll.description}</p>
              )}
              <p className="small text-muted mb-4">
                {t("totalVotes")}: {selectedPoll.total_votes}
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                {selectedPoll.results.map((result) => (
                  <div
                    key={result.option_id}
                    className="border rounded overflow-hidden"
                  >
                    {result.image_url ? (
                      <div style={{ position: "relative", paddingTop: "75%" }}>
                        <img
                          src={result.image_url}
                          alt={result.option_text}
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          paddingTop: "40%",
                          background: "#f0f2f5",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#adb5bd",
                            fontSize: "24px",
                          }}
                        >
                          📊
                        </div>
                      </div>
                    )}
                    <div style={{ padding: "10px" }}>
                      {result.option_text && (
                        <div className="small fw-semibold mb-1">
                          {result.option_text}
                        </div>
                      )}
                      <div
                        className="d-flex justify-content-between mb-1"
                        style={{ fontSize: "12px" }}
                      >
                        <span className="text-muted">
                          {result.vote_count} {t("votes")}
                        </span>
                        <span className="fw-bold text-primary">
                          {result.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <ProgressBar
                        now={result.percentage}
                        variant="primary"
                        style={{ height: "6px" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowResultsModal(false)}
          >
            {t("close")}
          </Button>
          {selectedPoll && (
            <Button
              variant="success"
              onClick={() => handleExportResults(selectedPoll.id)}
              disabled={exporting}
            >
              {exporting ? t("exporting") : `📥 ${t("exportToExcel")}`}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ══ PHASE 2 MODAL ══ */}
      <Modal
        show={showPhase2Modal}
        onHide={() => setShowPhase2Modal(false)}
        size="lg"
        dir={dir}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            🔗 {t("createPhase2")} — {phase2Parent?.title || t("untitled")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small py-2 mb-3">
            {t("phase2Desc")}
          </Alert>

          {phase2ParentResults && (
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                {t("phase2TriggerLabel")}
              </Form.Label>
              <p className="text-muted small mb-2">{t("phase2TriggerDesc")}</p>
              <div className="d-flex flex-wrap gap-2">
                {phase2ParentResults.options.map((opt) => {
                  const selected = phase2TriggerIds.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() =>
                        setPhase2TriggerIds((prev) =>
                          prev.includes(opt.id)
                            ? prev.filter((id) => id !== opt.id)
                            : [...prev, opt.id],
                        )
                      }
                      style={{
                        border: selected
                          ? "2px solid #0d6efd"
                          : "2px solid #dee2e6",
                        borderRadius: "8px",
                        padding: "6px 14px",
                        background: selected ? "#e8f0fe" : "#fff",
                        color: selected ? "#0d6efd" : "#495057",
                        fontWeight: selected ? 600 : 400,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.image_url && (
                        <img
                          src={opt.image_url}
                          alt=""
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "4px",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      {opt.option_text || (
                        <em className="text-muted">{t("noCaption")}</em>
                      )}
                      {selected && (
                        <span style={{ color: "#0d6efd", fontWeight: "bold" }}>
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {phase2TriggerIds.length > 0 && (
                <small className="text-success mt-2 d-block">
                  ✓{" "}
                  {`${phase2TriggerIds.length} ${t("phase2TriggersSelected")}`}
                </small>
              )}
            </Form.Group>
          )}

          <hr />

          <Form id="phase2-form" onSubmit={handleCreatePhase2}>
            <h6
              className="mb-3 text-muted text-uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.08em" }}
            >
              {t("phase2PollConfig")}
            </h6>
            <PollMetaFields
              values={phase2Poll}
              onChange={(k, v) => setPhase2Poll((p) => ({ ...p, [k]: v }))}
              t={t}
            />
            <Form.Group className="mb-3">
              <OptionGrid
                options={phase2Options}
                setOptions={setPhase2Options}
                fileRefs={phase2FileRefs}
                idPrefix="phase2"
                t={t}
                onImageSelect={handleImageSelect}
                onRemoveImage={removeImage}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPhase2Modal(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="phase2-form"
            disabled={loading || phase2TriggerIds.length === 0}
          >
            {loading ? t("creating") : `🔗 ${t("createPhase2")}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminPolls;
